import { checkIsNativeApp, checkNativeWalletSupport } from 'utils/nativeApp'
import { hasCardAdapter } from './adapters/card'
import { api } from 'rest/urls'

type Method = payments.PaymentMethod
type Methods = Method[] | undefined

const warned: Record<string, boolean> = {}
const warnOnce = (key: string, ...args: any[]) => {
  if (warned[key]) return
  warned[key] = true
  console.warn('[payments]', ...args)
}

export const isCardEmbedded = (method?: Method) =>
  method?.key === 'online_card' && method?.cardForm?.mode === 'embedded'

export const isWalletNative = (method?: Method) =>
  !!method?.walletForm && method.walletForm.mode === 'native'

export const isWalletRedirect = (method?: Method) =>
  !!method?.walletForm && method.walletForm.mode !== 'native'

/**
 * Środowisko portfela (TEST/PRODUCTION w Google Pay).
 * UWAGA: `walletForm.sandbox` nie jest opisany w kontrakcie - dopóki backend go nie potwierdzi,
 * bierzemy `cardForm.sandbox` z listy metod, a w ostateczności zakładamy produkcję.
 */
export const resolveWalletSandbox = (walletForm?: payments.WalletForm, methods?: Methods) => {
  if (typeof walletForm?.sandbox === 'boolean') return walletForm.sandbox

  const fromCard = methods?.find(method => typeof method?.cardForm?.sandbox === 'boolean')
  if (fromCard?.cardForm) return !!fromCard.cardForm.sandbox

  warnOnce('sandbox', 'brak walletForm.sandbox i cardForm.sandbox - zakładam produkcję (do potwierdzenia z backendem)')
  return false
}

/**
 * POS ID do sesji merchanta Apple Pay.
 * UWAGA: `walletForm.posId` też nie jest opisany w tabeli pól kontraktu - fallback jak wyżej.
 */
export const resolveWalletPosId = (walletForm?: payments.WalletForm, methods?: Methods) => {
  if (walletForm?.posId) return walletForm.posId

  const fromCard = methods?.find(method => !!method?.cardForm?.posId)
  if (fromCard?.cardForm?.posId) {
    warnOnce('posId', 'brak walletForm.posId - używam cardForm.posId (do potwierdzenia z backendem)')
    return fromCard.cardForm.posId
  }

  warnOnce('posIdMissing', 'brak walletForm.posId - sesja merchanta Apple Pay najpewniej się nie powiedzie')
  return ''
}

/** Nazwa pola pozycji wpłaty z tokenem - nigdy nie hardkodujemy `cardToken` / `walletToken`. */
export const getTokenField = (method?: Method) =>
  method?.cardForm?.tokenField || method?.walletForm?.tokenField || ''

export const buildTokenExtra = (method: Method, token: string) => {
  const field = getTokenField(method)
  if (!field) {
    warnOnce(`tokenField_${method?.key}`, 'brak tokenField w metodzie', method?.key, '- wysyłam wpłatę bez tokenu')
    return {}
  }
  return { [field]: token }
}

/**
 * Metody `online_card`, których nie umiemy obsłużyć: backend wystawił operatora
 * z formularzem u nas, a my nie mamy dla niego adaptera. Metoda jest wtedy ukrywana,
 * więc bez raportowania zniknęłaby po cichu i nikt by się nie dowiedział, że klienci
 * danego wystawcy nie mają jak zapłacić kartą.
 */
export const findUnsupportedCardMethods = (methods?: Methods) =>
  (methods || []).filter(method =>
    method.key === 'online_card' &&
    method.cardForm?.mode === 'embedded' &&
    !hasCardAdapter(method.cardForm))

/**
 * Opis kafelka metody. Dla zapisanej metody budujemy go z `savedMethod`, żeby klient
 * widział CZYM płaci ("Visa 1234"), a nie samo "Zapisana metoda".
 */
export const getMethodDescription = (method?: Method) => {
  if (method?.key === 'saved_method') {
    return method?.savedMethod?.name || method?.description
    
  } else {
    return method?.description
  }
}

/**
 * Adresy pomocnicze z odpowiedzi backendu (cardForm.sessionUrl, applePay.validationUrl)
 * bywają pełne, a bywają ścieżkami - w obu przypadkach mają zadziałać.
 */
export const resolveApiUrl = (path?: string | null) => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  return path.startsWith('/')
    ? new URL(path, new URL(api).origin).toString()
    : new URL(path, api).toString()
}

/** Nazwa sprzedawcy pokazywana w arkuszu portfela - tytuł strony jest per marka */
export const getMerchantLabel = () => document.title || window.location.hostname

export const toAmount = (value: unknown) => {
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : 0
}

/**
 * Synchroniczna część filtra dostępności metod.
 * Google Pay wymaga dodatkowo odpowiedzi asynchronicznej - patrz useAvailableMethods.
 */
export const isMethodSupported = (method?: Method): boolean => {
  switch (method?.key) {
    case 'online_card': {
      const cardForm = method.cardForm
      if (!cardForm) {
        warnOnce('cardFormMissing', 'metoda online_card bez cardForm - ukrywam')
        return false
      }
      // Inny tryb niż `embedded` = metoda zachowuje się jak `online`, czyli samo
      // przekierowanie na bramkę. To jest furtka na operatora, dla którego nie mamy
      // adaptera: backend ustawia `mode` inny niż `embedded` i front działa bez zmian.
      if (cardForm.mode !== 'embedded') return true
      // Brak adaptera raportuje useAvailableMethods - głośno, bo to błąd konfiguracji,
      // a nie normalny stan (patrz findUnsupportedCardMethods niżej).
      return hasCardAdapter(cardForm)
    }

    case 'applepay': {
      const walletForm = method.walletForm
      if (!walletForm || walletForm.mode !== 'native') return true
      // Apple Pay JS nie działa w WKWebView - w natywnej aplikacji o dostępności decyduje
      // appka (WalletPaymentManager + window.__NATIVE_APP__.wallets), NIE validationUrl
      // (to pole jest tylko dla sesji merchanta w Safari - patrz applePay.ts / §1.4a kontraktu).
      if (checkIsNativeApp()) return checkNativeWalletSupport('applepay')
      if (!walletForm.applePay?.validationUrl) return false
      // Apple Pay wymaga HTTPS. Na http (np. dev server) canMakePayments() nie zwraca false,
      // tylko RZUCA InvalidAccessError - stąd i sprawdzenie protokołu, i try/catch.
      if (window.location.protocol !== 'https:') {
        warnOnce('applePayInsecure', 'Apple Pay wymaga HTTPS - ukrywam metodę na', window.location.protocol)
        return false
      }
      const Session = window.ApplePaySession
      if (!Session || typeof Session.canMakePayments !== 'function') return false
      try {
        return Session.canMakePayments()
      } catch (error) {
        console.warn('[payments] ApplePaySession.canMakePayments() rzucił wyjątkiem', error)
        return false
      }
    }

    case 'saved_method':
      // backend wystawia tę metodę tylko przy aktywnym mandacie, ale gdyby przyszła
      // z metodą w stanie failed/expired, nie ma sensu jej pokazywać - i tak odbije
      return !method.savedMethod || method.savedMethod.status === 'active'

    case 'gpay': {
      const walletForm = method.walletForm
      if (!walletForm || walletForm.mode !== 'native') return true
      if (checkIsNativeApp()) return checkNativeWalletSupport('gpay')
      return true
    }

    default:
      return true
  }
}
